import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parse and sanitize numeric input with min/max clamping.
 * - Strips non-numeric characters via regex
 * - clampMin: false skips min enforcement (use on onChange, then clamp on blur)
 *
 * @returns `string` for partial mid-typing input ("-", "1.", or "" for non-numeric),
 *          `number` for valid parsed & clamped values. Callers should use `String(result)`
 *          to safely handle both cases.
 */
export function parseNumberOnly(
  value: string,
  min?: number,
  max?: number,
  isAllowFloat = false,
  roundedNumber = 2,
  clampMin = true,
): string | number {
  const foundNumber = value.match(isAllowFloat ? /^(-?\d*(\.\d*)?)?/ : /^(-?\d*)/);
  let numberOnly = foundNumber?.[0] ?? '';

  if (numberOnly === '-' || (isAllowFloat && numberOnly.endsWith('.'))) {
    return numberOnly;
  }

  const num = isAllowFloat ? parseFloat(numberOnly) : parseInt(numberOnly, 10);

  if (!isNaN(num)) {
    if (clampMin && min !== undefined && num < min) numberOnly = min.toString();
    if (max !== undefined && num > max) numberOnly = max.toString();

    return Math.round(+numberOnly * Math.pow(10, roundedNumber)) / Math.pow(10, roundedNumber);
  }

  return numberOnly;
}
